/* eslint-disable react-hooks/exhaustive-deps */
import { EventGroup, StatusCode, WebsocketTypes } from '@/api/enumerations';
import { getOversightNotificationsUnreadCount } from '@/api/oversight';
import { DelegatedAccessStatusUpdatePayload } from '@/api/oversight/types';
import { WebsocketContext } from '@/context/websocketMessage';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { useLocation, useNavigate } from 'react-router-dom';

type DefaultLayoutHook = ReturnType<typeof useDefaultLayout>;

interface UseOversightHook {
  oversightUserRole: string;
  currentTab: string;
  handleChangeTab: (value: string) => void;
  openNotifications: boolean;
  handleOpenNotifications: () => void;
  handleCloseNotifications: () => void;
  defaultLayout: DefaultLayoutHook['defaultLayout'];
  onLayoutChanged: DefaultLayoutHook['onLayoutChanged'];
  unreadCount: number;
  handleNotificationRead: () => void;
}

const unreadCountPollingMs = 30000;

// Únicos status cujo push gera notificação que conta no sino. As expirações e
// os avisos de expiração não têm websocket nenhum — só o polling os alcança.
const notifyingStatuses = ['approved', 'rejected'];

export default function useOversight(): UseOversightHook {
  const [openNotifications, setOpenNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const { oversightUserRole } = useTokenRefresh();
  const { websocketMessage } = useContext(WebsocketContext);

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'oversight-notifications',
    panelIds: openNotifications ? ['left', 'right'] : ['left'],
  });

  const currentTab = location.pathname.split('/').pop() ?? 'clients';

  const getUnreadCount = async (group?: EventGroup): Promise<void> => {
    try {
      const { data, detail } = await getOversightNotificationsUnreadCount(
        group
      );

      if (detail?.description) throw new Error(detail.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (!data) throw new Error('Algo deu errado, tente novamente.');

      setUnreadCount(data.unread);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  useEffect(() => {
    getUnreadCount();

    // O polling é a rede: cobre os tipos que o websocket não empurra. Vai como
    // background para não entrar na linha do tempo da sessão.
    const pollingId = setInterval(
      () => getUnreadCount(EventGroup.BACKGROUND),
      unreadCountPollingMs
    );

    return () => clearInterval(pollingId);
  }, []);

  useEffect(() => {
    if (!websocketMessage) return;

    const messageData = JSON.parse(websocketMessage.data);

    if (messageData.type !== WebsocketTypes.DELEGATED_ACCESS_STATUS_UPDATE) {
      return;
    }

    const payload = messageData.payload as DelegatedAccessStatusUpdatePayload;

    if (!notifyingStatuses.includes(payload.status)) return;

    // Refetch em vez de incrementar: o contador do backend já desconta as
    // notificações display_only.
    getUnreadCount(EventGroup.BACKGROUND);
  }, [websocketMessage]);

  const handleChangeTab = useCallback(
    (value: string): void => {
      navigate(`/oversight/${value}`);
    },
    [navigate]
  );

  const handleOpenNotifications = useCallback(
    (): void => setOpenNotifications(true),
    []
  );

  const handleCloseNotifications = useCallback(
    (): void => setOpenNotifications(false),
    []
  );

  const handleNotificationRead = useCallback((): void => {
    setUnreadCount((prev) => Math.max(prev - 1, 0));
  }, []);

  return {
    oversightUserRole,
    currentTab,
    handleChangeTab,
    openNotifications,
    handleOpenNotifications,
    handleCloseNotifications,
    defaultLayout,
    onLayoutChanged,
    unreadCount,
    handleNotificationRead,
  };
}
